import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Eye, RefreshCw, Truck, Users, ExternalLink, XCircle } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [refundItems, setRefundItems] = useState([]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/admin/orders${params}`);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${newStatus}`);
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeliverMyself = async (orderId) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/deliver-myself`);
      toast.success('Order assigned to you for delivery');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to assign order');
    }
  };

  const handleDispatchToDrivers = async (orderId) => {
    try {
      const response = await axios.put(`${API}/admin/orders/${orderId}/dispatch-to-drivers`);
      toast.success(response.data.message);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to dispatch order');
    }
  };

  const handleWhatsAppNotify = async (orderId) => {
    try {
      const response = await axios.get(`${API}/admin/orders/${orderId}/whatsapp-url`);
      window.open(response.data.whatsapp_url, '_blank');
    } catch (error) {
      toast.error('Failed to get WhatsApp link');
    }
  };

  const handleRefund = async (orderId) => {
    if (!window.confirm('Are you sure you want to refund the ENTIRE order?')) return;
    try {
      await axios.put(`${API}/admin/orders/${orderId}/refund`);
      toast.success('Full order refunded');
      fetchOrders();
      setDetailsOpen(false);
    } catch (error) {
      toast.error('Failed to refund order');
    }
  };

  const handleRefundItems = async (orderId) => {
    if (refundItems.length === 0) {
      toast.error('Select items to refund');
      return;
    }
    try {
      const response = await axios.put(`${API}/admin/orders/${orderId}/refund-items`, {
        item_ids: refundItems
      });
      toast.success(response.data.message);
      setRefundItems([]);
      fetchOrders();
      setDetailsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refund items');
    }
  };

  const toggleRefundItem = (productId) => {
    setRefundItems(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending_payment': return 'bg-yellow-100 text-yellow-800';
      case 'processing': case 'paid': return 'bg-blue-100 text-blue-800';
      case 'ready_for_pickup': return 'bg-purple-100 text-purple-800';
      case 'out_for_delivery': case 'in_transit': return 'bg-orange-100 text-orange-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': case 'refunded': return 'bg-red-100 text-red-800';
      default: return 'bg-stone-100 text-stone-800';
    }
  };

  const statuses = [
    'pending_payment', 'processing', 'ready_for_pickup',
    'out_for_delivery', 'delivered', 'cancelled', 'refunded'
  ];

  const needsDispatch = (order) => {
    return order.payment_status === 'paid' &&
      ['processing'].includes(order.status) &&
      !order.driver_id;
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-orders">
        <div className="flex justify-between items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px]" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              {statuses.map(s => (
                <SelectItem key={s} value={s}>{s.replace(/_/g, ' ').toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchOrders} data-testid="refresh-orders">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner"></div></div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-lg border border-stone-200 p-8 text-center text-stone-500">
            No orders found
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${needsDispatch(order) ? 'border-orange-300 ring-1 ring-orange-200' : 'border-stone-200'}`} data-testid={`order-row-${order.id}`}>
                <div className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-sm text-stone-600">#{order.id.slice(0, 8)}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusBadgeClass(order.status)}`}>
                          {order.status.replace(/_/g, ' ')}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusBadgeClass(order.payment_status)}`}>
                          {order.payment_status}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-stone-600">
                        <span>{order.items.length} items</span>
                        <span className="font-semibold text-green-700">R{order.total.toFixed(2)}</span>
                        <span>{formatDate(order.created_at)}</span>
                        <span className="truncate max-w-xs">{order.delivery_address}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {needsDispatch(order) && (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-700 hover:bg-green-800 text-white"
                            onClick={() => handleDeliverMyself(order.id)}
                            data-testid={`deliver-myself-${order.id}`}
                          >
                            <Truck className="w-4 h-4 mr-1" />
                            Deliver Myself
                          </Button>
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handleDispatchToDrivers(order.id)}
                            data-testid={`dispatch-drivers-${order.id}`}
                          >
                            <Users className="w-4 h-4 mr-1" />
                            Send to Drivers
                          </Button>
                        </>
                      )}

                      <Select
                        value={order.status}
                        onValueChange={(v) => handleStatusChange(order.id, v)}
                      >
                        <SelectTrigger className="w-[140px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statuses.map(s => (
                            <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Button size="sm" variant="ghost" onClick={() => handleWhatsAppNotify(order.id)} data-testid={`wa-notify-${order.id}`} title="WhatsApp notification">
                        <ExternalLink className="w-4 h-4 text-green-600" />
                      </Button>

                      <Button size="sm" variant="ghost" onClick={() => { setSelectedOrder(order); setRefundItems([]); setDetailsOpen(true); }} data-testid={`view-${order.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>

                      {order.payment_status === 'paid' && order.status !== 'refunded' && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleRefund(order.id)} data-testid={`refund-${order.id}`}>
                          Refund
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {order.driver_id === 'admin_self' && order.status === 'out_for_delivery' && (
                  <div className="bg-green-50 px-5 py-2 border-t border-green-200 flex items-center justify-between">
                    <span className="text-sm text-green-800 font-medium">You are delivering this order</span>
                    <Button size="sm" className="bg-green-700 hover:bg-green-800 h-7 text-xs" onClick={() => handleStatusChange(order.id, 'delivered')}>
                      Mark Delivered
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Order Details</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-stone-500">Order ID</p>
                    <p className="font-mono">{selectedOrder.id.slice(0, 12)}...</p>
                  </div>
                  <div>
                    <p className="text-stone-500">Date</p>
                    <p>{formatDate(selectedOrder.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-stone-500">Status</p>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(selectedOrder.status)}`}>
                      {selectedOrder.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div>
                    <p className="text-stone-500">Payment</p>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(selectedOrder.payment_status)}`}>
                      {selectedOrder.payment_status}
                    </span>
                  </div>
                </div>
                <div className="border-t border-stone-200 pt-4">
                  <p className="text-stone-500 text-sm mb-2">Delivery Address</p>
                  <p className="text-stone-900">{selectedOrder.delivery_address}</p>
                  <p className="text-stone-600">{selectedOrder.delivery_phone}</p>
                  {selectedOrder.delivery_notes && (
                    <p className="text-stone-500 text-sm mt-2">Notes: {selectedOrder.delivery_notes}</p>
                  )}
                </div>
                <div className="border-t border-stone-200 pt-4">
                  <p className="text-stone-500 text-sm mb-2">Items</p>
                  <div className="space-y-2">
                    {selectedOrder.items.map((item) => {
                      const isRefunded = (selectedOrder.refunded_items || []).includes(item.product_id);
                      const isSelected = refundItems.includes(item.product_id);
                      const canRefund = selectedOrder.payment_status === 'paid' || selectedOrder.payment_status === 'partially_refunded';
                      return (
                        <div key={item.product_id} className={`flex items-center justify-between text-sm p-2 rounded-lg ${isRefunded ? 'bg-red-50 line-through text-stone-400' : isSelected ? 'bg-yellow-50 border border-yellow-200' : ''}`}>
                          <div className="flex items-center gap-2">
                            {canRefund && !isRefunded && (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleRefundItem(item.product_id)}
                                className="w-4 h-4 rounded border-stone-300"
                                data-testid={`refund-check-${item.product_id}`}
                              />
                            )}
                            {isRefunded && <XCircle className="w-4 h-4 text-red-400" />}
                            <span>{item.quantity}x {item.name}</span>
                          </div>
                          <span className={`font-semibold ${isRefunded ? 'text-red-400' : ''}`}>
                            {isRefunded ? 'Refunded' : `R${item.subtotal.toFixed(2)}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {selectedOrder.refund_amount > 0 && (
                    <div className="flex justify-between mt-3 py-2 text-sm text-red-600">
                      <span>Refunded</span>
                      <span>-R{selectedOrder.refund_amount.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex justify-between mt-2 pt-4 border-t border-stone-200 font-bold">
                    <span>Total</span>
                    <span className="text-green-600">
                      R{(selectedOrder.adjusted_total ?? selectedOrder.total).toFixed(2)}
                    </span>
                  </div>

                  {refundItems.length > 0 && (
                    <Button
                      className="w-full mt-4 bg-yellow-600 hover:bg-yellow-700 text-white"
                      onClick={() => handleRefundItems(selectedOrder.id)}
                      data-testid="refund-selected-btn"
                    >
                      Refund {refundItems.length} Item(s) - Out of Stock
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
